import type { MapId, MatchSettings } from '@cratered/shared';

export interface CampaignLevel {
  id: string;
  index: number;
  name: string;
  blurb: string;
  map: MapId;
  seed: number;
  playerUnits: 1 | 2 | 3 | 4;
  enemies: { count: number; hp: number; difficulty: number; name: string };
  parRounds: number;
  turnSeconds: 15 | 30 | 45 | 60;
}

const NAMES: [string, string][] = [
  ['First Splash', 'Two raiders on the far raft. Tennis balls are free; so is the water.'],
  ['Grenade School', 'Learn the bounce. Three seconds is longer than you think.'],
  ['Mind the Gap', 'Two ledges over a drop. Kick someone and see what happens.'],
  ['Tower Rumble', 'Two towers, one field between, zero railings.'],
  ['Three Up', 'Outnumbered three to two. Pick them off.'],
  ['Crow’s Nest', 'A sniper up the crane. Bring the gun.'],
  ['Short Fuse', 'Fifteen-second turns. Decide fast.'],
  ['Tough Crowd', 'Raiders with thick hides.'],
  ['The Dock', 'They hold the high ground on the quay. Lure them down.'],
  ['Crossfire', 'Enemies on both sides. Watch your back.'],
  ['Night Shift', 'The keep after dark; the bots have steadied their aim.'],
  ['Cliffhanger', 'Fall damage will do the raiders’ work for them if you let it.'],
  ['Rock Hopper', 'The boardwalk. Narrow footing, long throws.'],
  ['Grenade Dan', 'They have grenades too. Keep moving.'],
  ['Halfway Hole', 'Four on three. Breathe.'],
  ['Headhunters', 'Gun practice. Aim high.'],
  ['Mesa Madness', 'Centre mesa, no cover. Who blinks first?'],
  ['Marksmen', 'The raiders rarely miss now.'],
  ['Deep Water', 'The marina crew. Splash kills pay double.'],
  ['Long Division', 'A very wide fight. Long shots pay extra XP.'],
  ['Rubble Rousers', 'Five raiders. Thin them before they swarm.'],
  ['Slippery Slope', 'The waterpark slides and short turns.'],
  ['The Gauntlet', 'Everything at once.'],
  ['Last Light', 'One mole against three. Comeback XP if you pull it off.'],
  ['Street Level', 'Twin Towers, six raiders, one street.'],
  ['Hard Hats', 'Raiders with 150 HP.'],
  ['Ridge Runners', 'The ridge, and they jump. A lot.'],
  ['Scuttled', 'The marina, six raiders, no mercy.'],
  ['Island Keepers', 'Tree Island at its hardest.'],
  ['The Big One', 'Everything you have learned. Three stars means flawless.'],
];

const MAPS: MapId[] = [
  'treeisle', 'treeisle', 'war:crossing', 'war:twin', 'beach', 'port', 'war:tiers', 'waterpark', 'port', 'war:crossing',
  'war:keep', 'war:hollow', 'beach', 'treeisle', 'war:twin', 'war:gate', 'war:pyramid', 'marina', 'marina', 'war:causeway',
  'war:crossing', 'waterpark', 'war:highlands', 'war:hills', 'war:twin', 'port', 'war:ridge', 'marina', 'treeisle', 'war:citadel',
];

export const CAMPAIGN: CampaignLevel[] = NAMES.map(([name, blurb], i) => {
  const n = i + 1;
  const tier = Math.floor(i / 6);
  const playerUnits = (i === 23 ? 1 : i < 4 ? 2 : i < 14 ? 3 : 4) as 1 | 2 | 3 | 4;
  const count = Math.min(6, 2 + Math.floor(i / 4) + (i === 20 || i === 24 || i === 27 ? 1 : 0));
  const hp = i === 25 ? 150 : 60 + tier * 15 + (i === 7 ? 30 : 0);
  const difficulty = Math.min(0.95, 0.3 + i * 0.022);
  return {
    id: `c${String(n).padStart(2, '0')}`,
    index: n,
    name,
    blurb,
    map: MAPS[i] ?? 'treeisle',
    seed: 1000 + n * 7919,
    playerUnits,
    enemies: { count, hp, difficulty, name: tier >= 3 ? 'Marauders' : 'Raiders' },
    parRounds: i === 23 ? 8 : 5 + Math.floor(i / 10),
    turnSeconds: i === 6 || i === 21 ? 15 : 30,
  };
});

export function campaignSettings(level: CampaignLevel): MatchSettings {
  return {
    mode: 'campaign',
    map: level.map,
    turnSeconds: level.turnSeconds,
    unitsPerTeam: level.playerUnits,
    teams: 2,
    parRounds: level.parRounds,
    wind: false,
    waves: 1,
    bestOf: 1,
    campaignLevelId: level.id,
  };
}

export function levelById(id: string | undefined): CampaignLevel | undefined {
  return CAMPAIGN.find((l) => l.id === id);
}

/** A level opens once the previous one has at least one star. */
export function isLevelOpen(level: CampaignLevel, stars: Record<string, number>): boolean {
  if (level.index === 1) return true;
  const prev = CAMPAIGN[level.index - 2];
  return (stars[prev.id] ?? 0) >= 1;
}
